import { SECTIONS } from '@/types/sections'
import first from 'lodash/first'
import isEmpty from 'lodash/isEmpty'
import last from 'lodash/last'
import { useDeepBoxPreviewStore } from '@/stores/deepbox/deepboxes/preview'
import { useNodeSection } from '../node/node-section'
import type { PathSegment } from '@deepcloud/api-deep-box'
import type { RouteLocationNamedRaw, RouteLocationPathRaw } from 'vue-router'

interface OriginRoute {
  text: string
  to: RouteLocationPathRaw | RouteLocationNamedRaw
}

export function usePreviewView() {
  const deepBoxPreviewStore = useDeepBoxPreviewStore()

  const isNodePreview = computed(
    () =>
      !isEmpty(deepBoxPreviewStore.currentNodeData?.path) &&
      !!deepBoxPreviewStore.currentNodeData?.deepBoxNodeId &&
      !!deepBoxPreviewStore.currentNodeData?.boxNodeId,
  )

  const { getNodeSectionTranslation, getNodeSectionTranslationByRouteName } =
    useNodeSection()

  // eslint-disable-next-line complexity
  const originRoute = computed((): OriginRoute => {
    if (!isNodePreview.value) {
      if (deepBoxPreviewStore.backRoute) {
        return {
          text: getNodeSectionTranslationByRouteName(
            (deepBoxPreviewStore.backRoute?.name ?? '') as string,
          ),
          to: {
            name: deepBoxPreviewStore.backRoute?.name,
            params: deepBoxPreviewStore.backRoute?.params,
            query: deepBoxPreviewStore.backRoute?.query,
          },
        }
      }
      return { text: '', to: { path: '/' } }
    }

    if (!deepBoxPreviewStore.currentNodeData)
      return { text: '', to: { path: '/' } }

    const lastSegment: PathSegment | undefined = last(
      deepBoxPreviewStore.currentNodeData?.path?.segments,
    )
    const firstSegment: PathSegment | undefined = first(
      deepBoxPreviewStore.currentNodeData?.path?.segments,
    )

    const isParentNodeNestedNode =
      deepBoxPreviewStore.currentNodeData?.path?.segments.length > 1 ||
      undefined

    if (deepBoxPreviewStore.backRoute) {
      let text = getNodeSectionTranslationByRouteName(
        (deepBoxPreviewStore.backRoute?.name ?? '') as string,
      )
      if (deepBoxPreviewStore.currentNodeData && isParentNodeNestedNode) {
        if (lastSegment?.displayName) {
          text = getNodeSectionTranslation(lastSegment?.displayName)
        }
      }

      return {
        text,
        to: {
          name: deepBoxPreviewStore.backRoute?.name,
          params: deepBoxPreviewStore.backRoute?.params,
          query: deepBoxPreviewStore.backRoute?.query,
        },
      }
    }

    let to: RouteLocationNamedRaw = {
      params: {
        type: deepBoxPreviewStore.currentNodeData?.deepBoxNodeId,
        box: deepBoxPreviewStore.currentNodeData?.boxNodeId,
      },
    }

    // if parent is nested node, then add the `node` param to route

    if (isParentNodeNestedNode) {
      to = {
        ...to,
        params: {
          ...to.params,
          node: lastSegment?.nodeId ?? '',
        },
      }
    }

    let text = lastSegment
      ? getNodeSectionTranslation(lastSegment.displayName)
      : ''
    let routeName = ''
    switch (firstSegment?.name) {
      case SECTIONS.FILES:
        if (isParentNodeNestedNode) {
          routeName = 'organization-types-type-boxes-box-files-node'
        } else {
          text = getNodeSectionTranslation(firstSegment?.name)
          routeName = 'organization-types-type-boxes-box-files'
        }
        break
      case SECTIONS.TRASH:
        if (isParentNodeNestedNode) {
          routeName = 'organization-types-type-boxes-box-trash-node'
        } else {
          routeName = 'organization-types-type-boxes-box-trash'
        }
        break
      case SECTIONS.QUEUE:
      case SECTIONS.INBOX:
        routeName = 'organization-types-type-boxes-box-inbox'
        break

      // no default
    }

    // adding the route name to Route `to`
    to = {
      ...to,
      name: routeName,
    }

    return { text, to }
  })

  return {
    isNodePreview,
    originRoute,
  }
}
